import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useToast } from '../hooks/useToast';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { MobileThieveryShell } from '../components/thievery/MobileThieveryShell';
import { DesktopThieveryShell } from '../components/thievery/DesktopThieveryShell';
import { formatNumber } from '../utils/format';
import {
  getOperationByType,
  estimateStealthCost,
  estimateSuccessRate,
  getTPAStrength,
} from '../constants/thievery';
import type { TpaStrength } from '../constants/thievery';
import {
  resolveDamageTier,
  estimateLossOnFailure,
  LOSS_RATE_BASE,
  LOSS_RATE_MAX,
  PREVIEW_TARGET_TPA,
} from '../utils/thieveryMath';
import { conductThieveryOperation } from '../api/thievery';
import { getKingdom } from '../api/magic';
import { formatProtectionCountdown } from '../utils/protection';
import type { Province } from '../types/province';
import type {
  DamageTier,
  ThieveryCategory,
  ThieveryOperation,
  ThieveryOperationType,
  ThieveryResult,
} from '../types/thievery';
import type { Kingdom, KingdomMember } from '../types/magic';

/**
 * SF-149: named prop types (Phase 3 Decision 13) — replace anonymous object shapes, matching the
 * ExploreShellProps/SciencesShellProps precedent of naming, not inlining, shell-props member types.
 */
export interface ThiefData {
  thieves: number;
  stealth: number;
  tpa: number;
}

export interface OperationInfo {
  operation: ThieveryOperation;
  stealthCost: number;
  successRate: number;
  estLoss: number;
}

/**
 * SF-149: named, fully-typed props shared by MobileThieveryShell/DesktopThieveryShell — same
 * ExploreShellProps/SciencesShellProps precedent. State stays hoisted here (not per-shell) so a
 * breakpoint flip mid-selection doesn't lose the in-progress target/operation selection.
 */
export interface ThieveryShellProps {
  province: Province;
  thiefData: ThiefData;
  tpaStrength: TpaStrength;
  hasNoThieves: boolean;
  kingdomNumber: string;
  setKingdomNumber: (value: string) => void;
  islandNumber: string;
  setIslandNumber: (value: string) => void;
  handleChangeKingdom: () => void;
  isLoadingKingdom: boolean;
  kingdomMembers: KingdomMember[];
  selectedTarget: string;
  handleSelectTarget: (targetId: string) => void;
  selectedTargetProtectionCountdown: string | null;
  selectedOperation: ThieveryOperationType | '';
  setSelectedOperation: (operation: ThieveryOperationType | '') => void;
  operation: ThieveryOperation | null;
  isIntel: boolean;
  damageTier: DamageTier;
  setDamageTier: (tier: DamageTier) => void;
  allowedTiers: DamageTier[];
  tierLabels: Partial<Record<DamageTier, string>>;
  thievesSentPreview: number;
  operationInfo: OperationInfo | null;
  submitDisabledReason: string | null;
  isSubmitting: boolean;
  submitError: string | null;
  handleConductOperation: () => void;
  operationResult: ThieveryResult | null;
}

export function Thievery() {
  const { provinceId, worldId } = useAuth();
  const { province, isLoading, error, updateProvince } = useProvince();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  const [kingdomNumber, setKingdomNumber] = useState('');
  const [islandNumber, setIslandNumber] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [kingdomMembers, setKingdomMembers] = useState<KingdomMember[]>([]);
  const [isLoadingKingdom, setIsLoadingKingdom] = useState(false);
  const [kingdomCache, setKingdomCache] = useState<Record<string, KingdomMember[]>>({});
  const [hasAutoLoadedKingdom, setHasAutoLoadedKingdom] = useState(false);

  const [selectedOperation, setSelectedOperation] = useState<ThieveryOperationType | ''>('');
  const [damageTier, setDamageTier] = useState<DamageTier>(() => {
    const saved = sessionStorage.getItem('thievery.damageTier');
    return saved === 'MINOR' || saved === 'MEDIUM' || saved === 'MASSIVE' ? saved : 'MEDIUM';
  });

  useEffect(() => {
    sessionStorage.setItem('thievery.damageTier', damageTier);
  }, [damageTier]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [operationResult, setOperationResult] = useState<ThieveryResult | null>(null);

  // SF-149 (mirrors SF-139's/SF-140's reportError/reportSuccess split): mobile routes feedback
  // through the app-wide toast system; desktop keeps its existing inline messages. Thievery has
  // three independent error-producing flows (handleChangeKingdom, handleConductOperation, and the
  // kingdom auto-load effect below) that all route through this same dispatch.
  const reportError = (message: string) => {
    if (platform === 'mobile') {
      addToast(message, 'failure');
    } else {
      setSubmitError(message);
    }
  };

  useEffect(() => {
    async function autoLoadOwnKingdom() {
      if (!province || hasAutoLoadedKingdom || !worldId) return;
      const { island, kingdom } = province.metadata.location;
      const kingdomId = `${kingdom}:${island}`;
      setIslandNumber(String(island));
      setKingdomNumber(String(kingdom));
      setHasAutoLoadedKingdom(true);
      if (kingdomCache[kingdomId]) {
        setKingdomMembers(kingdomCache[kingdomId]);
        return;
      }
      setIsLoadingKingdom(true);
      try {
        const kingdomData: Kingdom = await getKingdom(worldId, kingdomId);
        setKingdomMembers(kingdomData.members);
        setKingdomCache((prev) => ({ ...prev, [kingdomId]: kingdomData.members }));
      } catch (err) {
        reportError(err instanceof Error ? err.message : 'Failed to auto-load kingdom');
      } finally {
        setIsLoadingKingdom(false);
      }
    }
    autoLoadOwnKingdom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province, hasAutoLoadedKingdom, kingdomCache, worldId]);

  useEffect(() => {
    const targetId = searchParams.get('targetId');
    const targetLoc = searchParams.get('targetLoc');
    if (targetId && targetLoc) {
      const parts = targetLoc.split(':');
      if (parts.length === 2) {
        setKingdomNumber(parts[0]);
        setIslandNumber(parts[1]);
      }
      setSelectedTarget(targetId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const thiefData = useMemo<ThiefData | null>(() => {
    if (!province) return null;
    const thieves = province.population.thieves;
    const stealth = province.resources.stealth;
    const totalAcres = province.territory.totals.acres;
    const tpa = totalAcres > 0 ? thieves / totalAcres : 0;
    return { thieves, stealth, tpa };
  }, [province]);

  const handleChangeKingdom = async () => {
    setSubmitError(null);
    setOperationResult(null);
    if (!islandNumber || !kingdomNumber) {
      reportError('Please enter both island and kingdom numbers');
      return;
    }
    if (!worldId) {
      reportError('World ID not found');
      return;
    }
    const kingdomId = `${kingdomNumber}:${islandNumber}`;
    if (kingdomCache[kingdomId]) {
      setKingdomMembers(kingdomCache[kingdomId]);
      setSelectedTarget('');
      setSelectedOperation('');
      return;
    }
    setIsLoadingKingdom(true);
    try {
      const kingdom: Kingdom = await getKingdom(worldId, kingdomId);
      setKingdomMembers(kingdom.members);
      setKingdomCache({ ...kingdomCache, [kingdomId]: kingdom.members });
      setSelectedTarget('');
      setSelectedOperation('');
    } catch (err) {
      reportError(err instanceof Error ? err.message : 'Failed to load kingdom');
      setKingdomMembers([]);
    } finally {
      setIsLoadingKingdom(false);
    }
  };

  // SF-149 Phase 3 Decision 8: matches handleChangeKingdom's reset of selectedOperation above —
  // both target-changing paths now clear a stale operation selection identically.
  const handleSelectTarget = (targetId: string) => {
    setSelectedTarget(targetId);
    setSelectedOperation('');
  };

  const operation = selectedOperation ? getOperationByType(selectedOperation) : null;
  const isIntel = operation?.category === 'INTEL';
  const selectedTargetMember = kingdomMembers.find((m) => m.provinceId === selectedTarget) ?? null;
  const selectedTargetProtectionCountdown = selectedTargetMember
    ? formatProtectionCountdown(selectedTargetMember.protectionExpiresAtTick, province?.sync.currentTick ?? 0)
    : null;
  const allowedTiers = useMemo<DamageTier[]>(() => {
    if (selectedOperation === 'ARSON') return ['MEDIUM', 'MASSIVE'];
    return ['MINOR', 'MEDIUM', 'MASSIVE'];
  }, [selectedOperation]);

  useEffect(() => {
    if (!allowedTiers.includes(damageTier)) {
      setDamageTier(allowedTiers[0]);
    }
  }, [allowedTiers, damageTier]);

  const thievesSentPreview = useMemo(() => {
    if (!operation || !thiefData) return 0;
    if (isIntel) {
      const computed = Math.floor(thiefData.thieves * operation.thiefCostPercent);
      return thiefData.thieves > 0 ? Math.max(1, computed) : 0;
    }
    return resolveDamageTier(thiefData.thieves, operation.thiefCostPercent, damageTier);
  }, [operation, thiefData, isIntel, damageTier]);

  const operationInfo = useMemo<OperationInfo | null>(() => {
    if (!operation || !thiefData) return null;
    const stealthCost = estimateStealthCost(thiefData.stealth, selectedOperation as ThieveryOperationType);
    const successRate = estimateSuccessRate(thiefData.tpa, selectedOperation as ThieveryOperationType);
    const category = operation.category;
    const baseLoss = LOSS_RATE_BASE[category as ThieveryCategory];
    const estLoss = estimateLossOnFailure(
      thievesSentPreview,
      baseLoss,
      thiefData.tpa,
      PREVIEW_TARGET_TPA,
      LOSS_RATE_MAX,
    );
    return { operation, stealthCost, successRate, estLoss };
  }, [operation, selectedOperation, thiefData, thievesSentPreview]);

  const handleConductOperation = async () => {
    setSubmitError(null);
    setOperationResult(null);
    if (!province || !provinceId || !worldId || !thiefData || !operation) return;
    if (!selectedTarget) { reportError('Please select a target province'); return; }
    if (!selectedOperation) { reportError('Please select an operation to conduct'); return; }
    if (thiefData.thieves === 0) { reportError('You have no thieves available'); return; }
    if (thiefData.stealth < 10) { reportError('Insufficient stealth (minimum 10% required)'); return; }

    setIsSubmitting(true);
    try {
      const result = await conductThieveryOperation(worldId, provinceId, {
        targetProvinceId: selectedTarget,
        operationName: selectedOperation,
        damageTier: isIntel ? null : damageTier,
      });
      setOperationResult(result.result);
      if (result.province) updateProvince(result.province as Province);
    } catch (err) {
      reportError(err instanceof Error ? err.message : 'Failed to conduct operation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !province || !thiefData) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorMessage message={error || 'Province data not available'} />
        </div>
      </div>
    );
  }

  const tpaStrength = getTPAStrength(thiefData.tpa);
  const hasNoThieves = thiefData.thieves === 0;

  const tierLabels: Partial<Record<DamageTier, string>> = operation && !isIntel
    ? {
        MINOR: `~${formatNumber(
          resolveDamageTier(thiefData.thieves, operation.thiefCostPercent, 'MINOR'),
        )} thieves`,
        MEDIUM: `~${formatNumber(
          resolveDamageTier(thiefData.thieves, operation.thiefCostPercent, 'MEDIUM'),
        )} thieves`,
        MASSIVE: `~${formatNumber(
          resolveDamageTier(thiefData.thieves, operation.thiefCostPercent, 'MASSIVE'),
        )} thieves`,
      }
    : {};

  // SF-149 Phase 3 Decision 10: a single, always-current explanation of the submit button's
  // disabled state (4 independent causes, priority order below) — supersedes desktop's old
  // standalone "not enough thieves" line rather than supplementing it.
  const submitDisabledReason: string | null = !selectedOperation
    ? 'Select an operation to continue.'
    : hasNoThieves
    ? 'You have no thieves.'
    : selectedTargetProtectionCountdown
    ? 'Target is protected — operation unavailable.'
    : !isIntel && thievesSentPreview === 0
    ? `Not enough thieves for ${damageTier} tier; try a higher tier or train more thieves.`
    : null;

  const shellProps: ThieveryShellProps = {
    province,
    thiefData,
    tpaStrength,
    hasNoThieves,
    kingdomNumber,
    setKingdomNumber,
    islandNumber,
    setIslandNumber,
    handleChangeKingdom,
    isLoadingKingdom,
    kingdomMembers,
    selectedTarget,
    handleSelectTarget,
    selectedTargetProtectionCountdown,
    selectedOperation,
    setSelectedOperation,
    operation,
    isIntel,
    damageTier,
    setDamageTier,
    allowedTiers,
    tierLabels,
    thievesSentPreview,
    operationInfo,
    submitDisabledReason,
    isSubmitting,
    submitError,
    handleConductOperation,
    operationResult,
  };

  return platform === 'mobile'
    ? <MobileThieveryShell {...shellProps} />
    : <DesktopThieveryShell {...shellProps} />;
}
